import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import SearchBar from "../../components/SearchBar";
import DownloadIcon from "@mui/icons-material/Download";
import PaymentsList from "../../features/managePayments/paymentsList";
import usePermission from "../../hooks/usePermission";
import {
  useAcceptRejectPaymentMutation,
  useLazyGetPaymentListQuery,
} from "../../services/main";
import { isValidInput } from "../../utils/validations";
import TableComponent from "../../components/TableComponent";
import { GridColDef } from "@mui/x-data-grid";
import moment from "moment";
import { PAYMENT_STATUS } from "../../utils/enums";
import { showError, showToast } from "../../constants/toasts";

const ManagePayments = () => {
  const navigate = useNavigate();
  const [getMethod] = useLazyGetPaymentListQuery();
  const [updateStatus] = useAcceptRejectPaymentMutation();

  const [searchTerm, setSearchTerm] = useState("");
  const { isView } = usePermission({
    label: "Manage Payments",
    page_permission: "isView",
  });
  const [count, setCount] = useState(0);
  const [totalCommission, setTotalCommission] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [rows, setRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 180,
      field: "issue",
      headerName: "Issue Detail",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <figure className="no_radius">
              <img src={row?.issue?.image[0] || ""} alt="" />
            </figure>
            <p>{row?.issue?.name || ""}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 220,
      field: "customer",
      headerName: "Customer Detail",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            className="user_block click_action"
            onClick={() =>
              navigate(`/manage-customers/detail/${row?.customer?._id}`)
            }
          >
            <figure>
              <img
                src={
                  row?.customer?.image || "/static/images/user_placeholder.png"
                }
                alt=""
              />
            </figure>
            <p>
              <b>{row?.customer?.fullName || ""}</b>{" "}
              <span>{row?.customer?.email || ""}</span>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 220,
      field: "maintenance",
      headerName: "Maintenance Team Detail",
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div
            className="user_block click_action"
            onClick={() =>
              navigate(
                `/manage-maintenance-team/detail/${row?.maintainer?._id}`
              )
            }
          >
            <figure>
              <img
                src={
                  row?.maintainer?.image ||
                  "/static/images/user_placeholder.png"
                }
                alt=""
              />
            </figure>
            <p>
              <b>{row?.maintainer?.fullName || ""}</b>{" "}
              <span>{row?.maintainer?.email || ""}</span>
            </p>
          </div>
        );
      },
    },
    {
      minWidth: 150,
      field: "offered",
      headerName: "Offered Price",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p className="c_primary">
            <strong>AED {row?.price || "0"}</strong>
          </p>
        );
      },
    },
    {
      minWidth: 150,
      field: "commission",
      headerName: "Commission",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p className="c_success">
            <strong>AED {row?.adminCommission || "0"}</strong>
          </p>
        );
      },
    },
    {
      minWidth: 150,
      field: "total",
      headerName: "Total Amount",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        const adminCommission = Number(row?.adminCommission) || 0;
        const price = Number(row?.price) || 0;
        const total = adminCommission + price;

        return (
          <p>
            <strong>AED {total.toFixed(2)}</strong>
          </p>
        );
      },
    },

    {
      minWidth: 150,
      field: "createdAt",
      headerName: "Date",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return <span> {moment(row?.createdAt).format("YYYY-MM-DD")}</span>;
      },
    },
    {
      minWidth: 150,
      field: "status",
      headerName: "Status",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Select
            fullWidth
            disabled={row?.paymentStatus === PAYMENT_STATUS.SUCCEEDED}
            value={row?.paymentStatus ?? "default"}
            onChange={(e) => {
              handleStatusUpdate(Number(e.target.value), row?._id);
            }}
          >
            <MenuItem disabled value="default">
              Select Status
            </MenuItem>
            <MenuItem value={PAYMENT_STATUS.PENDING}>Pending</MenuItem>
            <MenuItem value={PAYMENT_STATUS.SUCCEEDED}>Completed</MenuItem>
          </Select>
        );
      },
    },
  ];

  const getData = async () => {
    try {
      setIsLoading(true);
      const response = await getMethod({
        page,
        limit: 10,
        search: debouncedSearchTerm.trim(),
      }).unwrap();
      if (response?.statusCode === 200) {
        setIsLoading(false);
        setRows(response?.data?.data || []);
        setCount(response?.data?.count);
        setTotalCommission(response?.data?.totalCommission);
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error, "00");
    }
  };

  const handleStatusUpdate = async (status: number, selectedId: string) => {
    console.log(status, "status");

    if (selectedId) {
      const body = {
        offerId: selectedId,
        paymentStatus: status,
      };
      console.log(body, ")))");

      try {
        const response = await updateStatus(body).unwrap();
        if (response?.statusCode === 200) {
          showToast("Payment status successfully");

          getData();
        }
      } catch (error: any) {
        console.log(error, "errror");
        showError(error?.data?.message || "");
      }
    }
  };
  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  useEffect(() => {
    getData();
  }, [debouncedSearchTerm, page]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Manage Payments</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> - Manage
          Payments
        </p>
      </div>

      <div className="stats_boxes gap_m">
        <div className="cards">
          <figure>
            <img src="/static/images/money_icon.svg" alt="" />
          </figure>
          <h2>${totalCommission || 0}</h2>
          <p>Total Commision Recieved</p>
        </div>
      </div>

      <div className="cards mt_20">
        <div className="table_header">
          <div className="left_s">
            <Box className="search_box">
              <SearchBar
                placeholder="Search..."
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </Box>
          </div>
          {/* <div className="right_s">
            <Button className="btn btn_grey">
              <DownloadIcon /> Export
            </Button>
          </div> */}
        </div>
        {isView && (
          <div className="sorting_table">
            <TableComponent
              columns={columns}
              rows={rows}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={isLoading}
            />
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default ManagePayments;
